import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Droplet, Plus, Check } from "lucide-react";
import { toast } from "sonner";

const VARIANTS = [
  { sku: "PL-BACT-3", strength: "3 ml" },
  { sku: "PL-BACT-10", strength: "10 ml" },
];

// Bact water upsell — shown on cart when a peptide is present, and inline
// on the product detail page. Renders BOTH 3ml and 10ml as side-by-side
// options. Each variant uses its own `bundle_price` discount (e.g. 3ml
// £4.99 → £3.99, 10ml £9.99 → £7.99) when added alongside a peptide.
// Out-of-stock variants hide their Add button.
export default function BactWaterUpsell({ items, addItem, hasPeptide }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    Promise.all(VARIANTS.map((v) => api.get(`/products/${v.sku}`).then((r) => r.data).catch(() => null)))
      .then((rows) => setProducts(rows.filter(Boolean)));
  }, []);

  if (products.length === 0) return null;

  const inCart = (sku) => items?.some?.((i) => i.sku === sku);
  const add = (p) => {
    const standalonePrice = p.price || 0;
    const bundlePrice = p.bundle_price || standalonePrice;
    const price = hasPeptide ? bundlePrice : standalonePrice;
    // Pass the untouched product — the cart applies bundle pricing itself
    // (and reverts it if the peptide is later removed), keeping the basket
    // in lockstep with server-side order pricing.
    addItem(p, 1);
    toast.success(`${p.name} ${p.strength} added at £${price.toFixed(2)}`, { duration: 1800 });
  };

  // Compute which variant is "best value" by effective price per ml.
  const byPerMl = (p) => (hasPeptide ? p.bundle_price : p.price) / parseFloat(p.strength);
  const bestSku = products
    .filter((p) => (p.stock_quantity || 0) > 0)
    .sort((a, b) => byPerMl(a) - byPerMl(b))[0]?.sku;

  return (
    <div
      className="pg-card p-5"
      style={{
        borderColor: "rgba(166,255,0,0.35)",
        background: "linear-gradient(135deg, rgba(166,255,0,0.04) 0%, rgba(94,234,212,0.04) 100%), #111A2B",
      }}
      data-testid="bact-upsell"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: "rgba(94,234,212,0.5)", background: "rgba(94,234,212,0.08)" }}
        >
          <Droplet size={20} className="text-[#5EEAD4]" />
        </div>
        <div className="min-w-0">
          <div className="text-[#F7FAFC] pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
            Bacteriostatic Water
          </div>
          <div className="text-xs text-[#9CA8B8]">
            Sterile diluent for research reconstitution. UK stock · same-day dispatch.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {products.map((p) => {
          const out = (p.stock_quantity || 0) <= 0;
          const standalone = p.price || 0;
          const bundle = p.bundle_price || standalone;
          const price = hasPeptide ? bundle : standalone;
          const saving = hasPeptide ? Math.max(0, standalone - bundle) : 0;
          const isBest = !out && p.sku === bestSku && products.length > 1;
          return (
            <div
              key={p.sku}
              className="p-3 border rounded-sm flex items-center justify-between gap-3 relative"
              style={{
                borderColor: isBest ? "rgba(166,255,0,0.55)" : "rgba(94,234,212,0.3)",
                background: isBest ? "rgba(166,255,0,0.04)" : "transparent",
                opacity: out ? 0.5 : 1,
              }}
              data-testid={`bact-variant-${p.sku}`}
            >
              {isBest && (
                <span
                  className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase pg-mono font-bold"
                  style={{ background: "#0B1220", color: "#A6FF00", border: "1px solid #A6FF00", borderRadius: 2 }}
                >
                  Best value
                </span>
              )}
              <div className="min-w-0">
                <div className="text-[#F7FAFC] pg-heading text-sm" style={{ fontFamily: "'Rajdhani'" }}>
                  {p.strength}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-[#A6FF00] pg-display text-lg leading-none" style={{ fontFamily: "'Exo 2'" }}>
                    £{price.toFixed(2)}
                  </span>
                  {saving > 0 && (
                    <span className="text-[#9CA8B8] text-[11px] line-through">£{standalone.toFixed(2)}</span>
                  )}
                </div>
                {saving > 0 && (
                  <div className="pg-mono text-[10px] text-[#5EEAD4] mt-0.5 tracking-[0.12em]">
                    Bundle · save £{saving.toFixed(2)}
                  </div>
                )}
              </div>
              {out ? (
                <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]" data-testid={`bact-oos-${p.sku}`}>
                  Out of stock
                </span>
              ) : inCart(p.sku) ? (
                <span className="pg-btn-ghost text-xs whitespace-nowrap" data-testid={`bact-in-cart-${p.sku}`}>
                  <Check size={14} /> In basket
                </span>
              ) : (
                <button
                  onClick={() => add(p)}
                  className="pg-btn-primary text-xs whitespace-nowrap"
                  data-testid={`bact-add-${p.sku}`}
                >
                  <Plus size={14} /> Add
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
